import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchContestGameLogs } from '@/api/stats';
import { GameLogLeaderboardEntry } from '@/api/schemas';
import { getAbbreviation } from '@/utils/teams';
import Spinner from '@/components/common/Spinner';

// Top 10 by VALUE — measured as $ paid per fantasy point (salary / points).
// Lower = better (less money spent per point produced), so rank 1 is the lowest $/Pt.
// Currency format displays naturally; "$396 per point" reads at a glance, whereas a
// raw points-per-$1k ratio (2.52) requires interpretation. The $/Pt column is highlighted
// in yellow as the sort key.
//
// Trimmed column set vs Top10Table: only Rank / Player / Sal / Pts / $/Pt are shown — no
// opponent or per-stat breakdown. The reader's question here is "is this player a bargain?",
// which only needs the three numbers in the formula.
//
// Implementation note: the contest game-logs endpoint doesn't expose a $/Pt sort key,
// so we fetch a wide pool sorted by points, compute $/Pt client-side, then slice to 10.

interface TopValueTableProps {
  heading: string;
  contestId: number;
  token: string | null;
  onPlayerClick?: (entry: GameLogLeaderboardEntry) => void;
}

const formatSalary = (n: number | null | undefined): string =>
  n != null ? `$${n.toLocaleString('en-US')}` : '—';

const formatPts = (n: number): string => n.toFixed(1);

// Format as whole-dollar currency: "$396". No decimals — pennies-per-point isn't useful.
const formatCostPerPoint = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

// $/Pt: salary divided by fantasy points produced. Lower = better.
// Returns Infinity for non-scoring or zero-salary rows so they sort to the bottom and
// can be filtered out before display.
const computeCostPerPoint = (row: GameLogLeaderboardEntry): number => {
  if (!row.dfs_salary || row.dfs_salary <= 0 || !row.fantasy_points || row.fantasy_points <= 0) {
    return Infinity;
  }
  return row.dfs_salary / row.fantasy_points;
};

/** Compute rank strings with T- prefix for ties. Detected on the supplied value array
 *  (already sorted, ascending or descending — the function only checks for adjacent equality). */
const computeRanksByValue = (values: number[]): string[] => {
  const ranks: string[] = [];
  let i = 0;
  while (i < values.length) {
    let j = i + 1;
    while (j < values.length && values[j] === values[i]) j++;
    const tieCount = j - i;
    const rankNum = i + 1;
    const prefix = tieCount > 1 ? `T-${rankNum}` : String(rankNum);
    for (let k = i; k < j; k++) ranks.push(prefix);
    i = j;
  }
  return ranks;
};

const RankBadge: React.FC<{ rank: string }> = ({ rank }) => {
  const tied = rank.toUpperCase().startsWith('T');
  const sizing = tied
    ? 'min-w-[38px] sm:min-w-[52px] h-[24px] sm:h-[30px] px-1 sm:px-2 text-badge sm:text-[12px] tracking-[0.5px] sm:tracking-[1px]'
    : 'min-w-[28px] sm:min-w-[40px] h-[24px] sm:h-[30px] px-2 sm:px-3 text-caption sm:text-sm';
  return (
    <span
      className={[
        'inline-flex items-center justify-center rounded-md',
        'bg-accent-yellow text-primary',
        'font-heading font-bold tracking-wide',
        'shadow-[0_2px_8px_rgba(255,196,44,0.25)]',
        sizing,
      ].join(' ')}
    >
      {rank}
    </span>
  );
};

const TeamPosPill: React.FC<{ team: string | null | undefined; position: string | null | undefined }> = ({
  team,
  position,
}) => {
  if (!team && !position) return null;
  return (
    <span className="ml-1.5 sm:ml-2 inline-block align-middle px-1.5 sm:px-2 py-0 sm:py-0.5 rounded bg-gray-200/10 text-gray-300 font-mono font-bold text-micro sm:text-badge tracking-wider whitespace-nowrap">
      {team || '—'}
      {team && position && <span className="text-gray-500"> · </span>}
      {position && <span className="text-accent-yellow">{position}</span>}
    </span>
  );
};

const TopValueTable: React.FC<TopValueTableProps> = ({ heading, contestId, token, onPlayerClick }) => {
  const { data: pool = [], isLoading, error } = useQuery({
    queryKey: ['blog-top-value', contestId],
    queryFn: () =>
      fetchContestGameLogs(
        contestId,
        {
          // Pull a wide pool sorted by points, then re-rank client-side by value.
          // 200 is enough headroom for any single contest's player set.
          sort_by: 'fantasy_points',
          order: 'desc',
          limit: 200,
        },
        token
      ),
    enabled: !!contestId,
    refetchOnWindowFocus: false,
  });

  const { rows, values, ranks } = useMemo(() => {
    const scored = pool
      .map(row => ({ row, value: computeCostPerPoint(row) }))
      .filter(x => Number.isFinite(x.value))
      .sort((a, b) => a.value - b.value) // ascending: lowest $/Pt is best value
      .slice(0, 10);

    const rows = scored.map(x => x.row);
    const values = scored.map(x => x.value);
    const ranks = computeRanksByValue(values);
    return { rows, values, ranks };
  }, [pool]);

  return (
    <div className="w-full mb-6">
      <div className="relative overflow-hidden rounded-lg border border-accent-yellow/60 bg-surface-card shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-accent-yellow/10 border-b border-accent-yellow/60">
                <th
                  colSpan={2}
                  className="text-left pl-2 sm:pl-6 py-3 sm:py-4 font-heading font-bold text-sm sm:text-lg tracking-[1.5px] sm:tracking-[2px] uppercase text-white whitespace-nowrap sticky left-0 bg-surface-raised z-10"
                >
                  {heading}
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  Sal
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  Pts
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-accent-yellow/10 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  $/Pt
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={99} className="py-12 text-center">
                    <Spinner size="md" />
                  </td>
                </tr>
              )}
              {!isLoading && error && (
                <tr>
                  <td colSpan={99} className="py-8 text-center text-secondary italic text-sm">
                    Could not load this table.
                  </td>
                </tr>
              )}
              {!isLoading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan={99} className="py-8 text-center text-gray-500 italic text-sm">
                    No performances yet.
                  </td>
                </tr>
              )}
              {!isLoading && !error && rows.map((row, idx) => {
                const isEven = idx % 2 === 1;
                const isLast = idx === rows.length - 1;
                const isClickable = !!onPlayerClick;
                return (
                  <tr
                    key={`${row.player_id}-${row.match_id}`}
                    className={[
                      isEven ? 'bg-primary-dark/30' : '',
                      isLast ? '' : 'border-b border-gray-700/40',
                      isClickable ? 'cursor-pointer hover:bg-primary/50 transition-colors duration-150 ease-in-out' : '',
                    ].join(' ')}
                    onClick={isClickable ? () => onPlayerClick!(row) : undefined}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={
                      isClickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onPlayerClick!(row);
                            }
                          }
                        : undefined
                    }
                  >
                    <td className="pl-2 pr-1 sm:pl-6 sm:pr-2 py-3 sm:py-4 text-left whitespace-nowrap sticky left-0 bg-surface-card z-[5]">
                      <RankBadge rank={ranks[idx] || String(idx + 1)} />
                    </td>

                    <td className="py-3 sm:py-4 pr-2 text-left whitespace-nowrap sticky left-[50px] sm:left-[88px] bg-surface-card z-[5]">
                      <span
                        className={[
                          'font-body font-bold text-[13px] sm:text-base text-white tracking-tight',
                          isClickable ? 'hover:text-accent-yellow hover:underline underline-offset-2' : '',
                        ].join(' ')}
                      >
                        {row.player_name}
                      </span>
                      <TeamPosPill
                        team={getAbbreviation(row.current_team_name) || row.current_team_name}
                        position={row.primary_position}
                      />
                    </td>

                    {/* Salary — visual numerator: Sal / Pts = $/Pt reads left-to-right. */}
                    <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap font-mono font-semibold text-sm text-white">
                      {formatSalary(row.dfs_salary)}
                    </td>

                    {/* PTS — visual denominator. */}
                    <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap font-mono font-semibold text-sm text-white">
                      {formatPts(row.fantasy_points)}
                    </td>

                    {/* $/Pt — headline column, highlighted yellow. Lower = better value. */}
                    <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap font-heading font-bold text-lg sm:text-xl tracking-wider text-accent-yellow bg-accent-yellow/5">
                      {formatCostPerPoint(values[idx] ?? 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TopValueTable;
